import happy from './assets/emotions/happy.png';
import veryhappy from './assets/emotions/very-happy.png';
import sad from './assets/emotions/sad.png';

export const journeyStepsUnbranded = [
  {
    chapter: "Chapter 1",
    title: "Your First Paycheck – Where Does It Go?",
    question:
      "You just received your first paycheck, and you want to make a smart money move.",
    answers: [
      {
        emoji: "💰",
        heading: "Keep it in a savings account",
        text: "Safe, but money won’t grow much over time.",
        points: 5,
        add: true,
        feedback: {
          outcome: "You’ve chosen safety, but is it the best move?",
          lesson: "Keeping money in a savings account is safe, but over time, inflation erodes its value. The average interest rate on savings accounts is often lower than inflation, meaning your money loses purchasing power over time.",
          tip: "Learn More:",
          linkText: "Learn about investing",
          url: "https://www.investopedia.com/terms/i/investing.asp",
        },
        image: sad,
      },
      {
        emoji: "📈",
        heading: "Invest in the stock market",
        text: "Potentially high reward, but also risky if not diversified.",
        points: 10,
        add: true,
        feedback: {
          outcome: "You’ve taken a step into the investing world, but is it the best strategy?",
          lesson: "Investing in individual stocks can lead to high returns, but it’s also high risk. If you put all your money in one company and it fails, you lose everything. Professional investors spread their money across different assets (diversification) to reduce risk.",
          tip: "Learn More:",
          linkText: "Investing basics",
          url: "https://www.investopedia.com/investing-4427685",
        },
        image: happy, 
      },
      {
        emoji: "🏦",
        heading: "Open a Stocks & Shares ISA",
        text: "A tax-efficient way to invest, but market risk still applies.",
        points: 15,
        add: true,
        feedback: {
          outcome: "Great choice! You’re investing tax-efficiently and thinking long-term",
          lesson: "A Stocks & Shares ISA lets you invest in a mix of stocks, bonds, and funds, while shielding your profits from tax. Unlike a regular savings account, it protects your money from inflation and allows it to grow over time.",
          tip: "Learn More:",
          linkText: "Stocks and Shares ISA",
          url: "https://www.investopedia.com/terms/i/isa.asp",
        },
        image: veryhappy, 
      },
    ],
  },
  {
    chapter: "Chapter 2",
    title: "Diversifying Investments – Where Do You Put Your Money?",
    question: "You’ve started investing, but diversification is key. What’s your next move?",
    answers: [
      {
        emoji: "📈",
        heading: "Put everything in a single stock",
        text: "Potentially high returns, but very risky!",
        points: 5,
        add: true,
        feedback: {
          outcome: "You’re taking a big risk—will it pay off?",
          lesson: "Betting everything on a single stock could lead to huge rewards if it performs well, but if it drops, you lose everything, making diversification a much safer strategy.",
          tip: "Learn How to Diversify Your Investments:",
          linkText: "Guide to Multi-Asset Funds",
          url: "https://www.investopedia.com/terms/m/multi-asset-fund.asp",
        },
        image: sad, 
      },
      {
        emoji: "📊",
        heading: "Invest in a Pension Fund",
        text: "Long-term security, but money is locked until retirement.",
        points: 15,
        add: true,
        feedback: {
          outcome: "Smart choice! You’re securing your future, but keep in mind pensions are long-term!",
          lesson: "A pension fund is one of the best long-term investments. You get tax benefits, employer contributions (if applicable), and compounding growth over decades. However, the trade-off is that you can’t access your pension until later in life.",
          tip: "Learn More & Plan Your Pension:",
          linkText: "What is a pension?",
          url: "https://www.investopedia.com/terms/p/pensionplan.asp",
        },
        image: veryhappy, 
      },
      {
        emoji: "💼",
        heading: "Open a General Investment Account (GIA)",
        text: "More flexible than a pension, but no tax relief.",
        points: 10,
        add: true,
        feedback: {
          outcome: "Good move! You get more flexibility, but there are no tax benefits.",
          lesson: "A General Investment Account (GIA) is a good option if you’ve maxed out your ISA contributions or need more access to your investments. Unlike a pension, there are no restrictions on when you can withdraw, but you might pay tax on your gains.",
          tip: "Compare GIAs & ISAs:",
          linkText: "General Investment Account",
          url: "https://www.investopedia.com/terms/g/general-investment-account.asp",
        },
        image: happy, 
      },
    ],
  },
  {
    chapter: "Chapter 3",
    title: "Market Volatility – What’s Your Next Move?",
    question:
      "You’re finally feeling financially secure… until an unexpected expense hits you—a surprise home repair! How do you handle it?",
    answers: [
      {
        emoji: "💼",
        heading: "Stick with a diversified investment portfolio",
        text: "Long-term stability reduces short-term panic.",
        points: 15,
        add: true,
        feedback: {
          outcome: "Great move! Investing is a long game—patience pays off.",
          lesson: "Staying invested in a diversified portfolio allows you to ride out market fluctuations and benefit from long-term growth, rather than reacting to short-term drops.",
          tip: "Learn How to Handle Market Ups & Downs:",
          linkText: "Investing in the stock market",
          url: "https://www.investopedia.com/terms/s/stockmarket.asp",
        },
        image: veryhappy, 
      },
      {
        emoji: "📉",
        heading: "Panic and sell everything",
        text: "Selling in a crash locks in losses!",
        points: 10,
        add: false,
        feedback: {
          outcome: "Selling when prices drop locks in your losses!",
          lesson: "Selling during a market crash turns a temporary paper loss into a real loss, preventing you from benefiting when the market recovers over time.",
          tip: "Understanding Risk & Emotional Investing:",
          linkText: "What is investing?",
          url: "https://www.investopedia.com/terms/i/investing.asp",
        },
        image: happy, 
      },
      {
        emoji: "💰",
        heading: "Shift everything to a savings account",
        text: "Safe, but you miss potential market recovery.",
        points: 5,
        add: true,
        feedback: {
          outcome: "You avoided risk, but you also locked in lower returns.",
          lesson: "Moving everything to cash protects against losses, but it also limits potential growth since savings accounts often fail to keep up with inflation.",
          tip: "When to Save vs. When to Invest:",
          linkText: "Investment & Savings Guide",
          url: "https://www.investopedia.com/terms/s/savings.asp",
        },
        image: sad, 
      },
    ],
  },
  {
    chapter: "Chapter 4",
    title: "You want to secure your future. What’s your strategy?",
    question:
      "You want to secure your future. What’s your strategy?",
    answers: [
      {
        emoji: "💼",
        heading: "Open a Workplace Pension or Personal Pension",
        text: "Tax benefits, employer contributions, but money is locked away.",
        points: 15,
        add: true,
        feedback: {
          outcome: "Fantastic! You’re maximizing tax benefits and employer contributions.",
          lesson: "A pension is one of the most tax-efficient ways to invest, with added employer contributions and government tax relief helping your money grow faster over time.",
          tip: "Plan for Your Retirement:",
          linkText: "Pension Calculator",
          url: "https://www.investopedia.com/terms/p/pensionplan.asp",
        },
        image: veryhappy, 
      },
      {
        emoji: "📈",
        heading: "Invest through a General Investment Account (GIA)",
        text: "Good flexibility, but lacks pension tax benefits.",
        points: 10,
        add: true,
        feedback: {
          outcome: "Good move, but a pension gives better tax benefits.",
          lesson: "A GIA lets you invest flexibly and withdraw anytime, but it lacks the tax advantages of a pension, meaning you might pay capital gains tax on your returns.",
          tip: "Compare Pensions vs. GIAs:",
          linkText: "Your Options",
          url: "https://www.investopedia.com/terms/g/general-investment-account.asp",
        },
        image: happy, 
      },
      {
        emoji: "🕰",
        heading: "Delay investing for retirement",
        text: "Waiting makes it harder later!",
        points: 5,
        add: false,
        feedback: {
          outcome: "The later you start, the harder it is to save!",
          lesson: "The earlier you start saving for retirement, the less you need to save each month, thanks to the power of compound interest working over decades.",
          tip: "Why Starting Early Matters:",
          linkText: "Investment Funds",
          url: "https://www.investopedia.com/terms/f/fund.asp",
        },
        image: sad, 
      },
    ],
  },
  {
    chapter: "Chapter 5",
    title: "Investing in Property – What’s the Best Move?",
    question:
      "You want to buy a home in the future. What’s your strategy?",
    answers: [
      {
        emoji: "🏦",
        heading: "Open a Lifetime ISA (LISA).",
        text: "Gov bonus helps, but limited annual contributions.",
        points: 15,
        add: true,
        feedback: {
          outcome: "Great strategy! The government helps boost your savings.",
          lesson: "A Lifetime ISA helps first-time homebuyers save faster by offering a 25% government bonus, but it has an annual contribution limit.",
          tip: "Learn More:",
          linkText: "Guide to Saving for a Home",
          url: "https://www.investopedia.com/terms/l/lifetime-isa.asp",
        },
        image: veryhappy, 
      },
      {
        emoji: "💳",
        heading: "Use most of your investments to buy ASAP",
        text: "Gets you in a home, but might not be the best timing.",
        points: 5,
        add: true,
        feedback: {
          outcome: "You got your home, but was the timing right",
          lesson: "Buying property is a long-term investment, but if you cash out stocks at the wrong time, you may miss out on future gains.",
          tip: "Plan Your Home Purchase Wisely:",
          linkText: "Guide to Saving for a Home",
          url: "https://www.investopedia.com/terms/l/lifetime-isa.asp",
        },
        image: sad, 
      },
      {
        emoji: "🚀",
        heading: "Invest in cryptocurrency hoping to afford a home faster",
        text: "High volatility makes this risky!",
        points: 10,
        add: false,
        feedback: {
          outcome: "Exciting, but extremely risky!",
          lesson: "While crypto has created quick gains for some, it remains highly volatile, meaning you could lose most of your investment overnight.",
          tip: "Understanding Investment Risk:",
          linkText: "Guide to Investment Risks",
          url: "https://www.investopedia.com/terms/c/cryptocurrency.asp",
        },
        image: happy, 
      },
    ],
  },
];
